import { useState } from 'react';
import { Archive, ArchiveRestore, Plus, X } from 'lucide-react';
import type { StoreroomDto } from '@eombor/contracts';
import { Badge, Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import {
  useAddZone,
  useCreateStoreroom,
  useRemoveZone,
  useUpdateStoreroom,
} from '../model/subdivision.queries.js';

export interface StoreroomDialogProps {
  open: boolean;
  /** `null` — yangi ombor; aks holda shu omborning sozlamalari. */
  storeroom: StoreroomDto | null;
  onClose: () => void;
  /** Yangi ombor yaratilgach — tanlagich uni darhol tanlab qo'yishi uchun. */
  onCreated?: (created: StoreroomDto) => void;
}

/**
 * OMBOR OYNASI — yaratish va sozlash bitta shaklda.
 *
 * Yaratishda zonalar QORALAMA bo'lib to'planadi va omborning o'zi bilan
 * bitta so'rovda ketadi: yarim yaratilgan (zonasi yo'q) ombor qolmasin.
 * Tahrirda esa har zona darhol yoziladi — «Saqlash» faqat nom va maqsad
 * uchun, aks holda o'chirilgan zona bilan saqlanmagan nom aralashib ketardi.
 */
export const StoreroomDialog = ({ open, storeroom, onClose, onCreated }: StoreroomDialogProps) => {
  const editing = storeroom !== null;
  const readOnly = editing && !storeroom.canManage;

  const [name, setName] = useState(storeroom?.name ?? '');
  const [purpose, setPurpose] = useState(storeroom?.purpose ?? '');
  const [draftZones, setDraftZones] = useState<string[]>([]);
  const [zoneName, setZoneName] = useState('');

  const create = useCreateStoreroom();
  const update = useUpdateStoreroom();
  const addZone = useAddZone();
  const removeZone = useRemoveZone();

  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error ?? addZone.error ?? removeZone.error;

  const zones: Array<{ key: string; name: string; busy: boolean; id: string | null }> = editing
    ? storeroom.zones.map((z) => ({ key: z.id, name: z.name, busy: z.lotCount > 0, id: z.id }))
    : draftZones.map((z) => ({ key: z, name: z, busy: false, id: null }));

  const pushZone = (): void => {
    const value = zoneName.trim();
    if (value === '') return;
    if (zones.some((z) => z.name.toLowerCase() === value.toLowerCase())) {
      setZoneName('');
      return;
    }
    if (editing) {
      addZone.mutate({ id: storeroom.id, name: value }, { onSuccess: () => setZoneName('') });
    } else {
      setDraftZones((prev) => [...prev, value]);
      setZoneName('');
    }
  };

  const dropZone = (zone: (typeof zones)[number]): void => {
    if (editing && zone.id !== null) {
      removeZone.mutate({ id: storeroom.id, zoneId: zone.id });
    } else {
      setDraftZones((prev) => prev.filter((z) => z !== zone.name));
    }
  };

  const submit = (): void => {
    const trimmed = name.trim();
    if (trimmed === '') return;
    if (editing) {
      update.mutate(
        { id: storeroom.id, input: { name: trimmed, purpose: purpose.trim() === '' ? null : purpose.trim() } },
        { onSuccess: onClose },
      );
      return;
    }
    create.mutate(
      { name: trimmed, purpose: purpose.trim() === '' ? null : purpose.trim(), zones: draftZones },
      {
        onSuccess: (created) => {
          onCreated?.(created);
          onClose();
        },
      },
    );
  };

  const toggleArchive = (): void => {
    if (!editing) return;
    update.mutate({ id: storeroom.id, input: { isActive: !storeroom.isActive } }, { onSuccess: onClose });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('storeroom.editTitle') : t('storeroom.createTitle')}
      description={readOnly ? t('storeroom.readOnly') : t('storeroom.hint')}
      busy={busy}
      dirty={!editing && (name !== '' || draftZones.length > 0)}
      {...(readOnly ? {} : { onSubmit: submit })}
      error={error === null ? undefined : error.message}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-3">
          {editing && !readOnly ? (
            <Button
              variant="ghost"
              className="mr-auto"
              disabled={busy || (storeroom.isActive && storeroom.lotCount > 0)}
              title={storeroom.isActive && storeroom.lotCount > 0 ? t('storeroom.archiveBlocked') : undefined}
              onClick={toggleArchive}
            >
              {storeroom.isActive ? <Archive size={16} aria-hidden /> : <ArchiveRestore size={16} aria-hidden />}
              {storeroom.isActive ? t('storeroom.archive') : t('storeroom.restore')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {readOnly ? t('storeroom.close') : t('storeroom.cancel')}
          </Button>
          {readOnly ? null : (
            <Button loading={busy} disabled={name.trim() === ''} onClick={submit}>
              {editing ? t('storeroom.save') : t('storeroom.create')}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('storeroom.name')}
            placeholder={t('storeroom.namePlaceholder')}
            maxLength={120}
            required
            disabled={readOnly}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label={t('storeroom.purpose')}
            placeholder={t('storeroom.purposePlaceholder')}
            maxLength={300}
            disabled={readOnly}
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
          />
        </div>

        <section className="flex flex-col gap-3" aria-label={t('storeroom.zonesTitle')}>
          <header className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-text">{t('storeroom.zonesTitle')}</h3>
            <p className="text-xs text-text-muted">{t('storeroom.zonesHint')}</p>
          </header>

          {zones.length === 0 ? (
            <p className="text-sm text-text-faint">{t('storeroom.noZones')}</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {zones.map((zone) => (
                <li
                  key={zone.key}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border border-border bg-surface-2',
                    'py-1 pr-1 pl-3 text-sm text-text',
                  )}
                >
                  {zone.name}
                  {readOnly ? (
                    <span className="w-1" />
                  ) : (
                    <button
                      type="button"
                      aria-label={t('storeroom.zoneRemove', { name: zone.name })}
                      title={zone.busy ? t('storeroom.zoneBusy') : t('storeroom.zoneRemove', { name: zone.name })}
                      disabled={zone.busy || removeZone.isPending}
                      onClick={() => dropZone(zone)}
                      className={cn(
                        'flex size-6 items-center justify-center rounded-full text-text-muted',
                        'hover:bg-danger-soft hover:text-danger disabled:cursor-not-allowed disabled:opacity-40',
                        'focus-visible:outline-2 focus-visible:outline-accent',
                      )}
                    >
                      <X size={13} aria-hidden />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {readOnly ? null : (
            <div className="flex items-end gap-2">
              <Input
                aria-label={t('storeroom.zoneAdd')}
                placeholder={t('storeroom.zonePlaceholder')}
                maxLength={120}
                value={zoneName}
                onChange={(event) => setZoneName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    pushZone();
                  }
                }}
              />
              <Button
                variant="secondary"
                disabled={zoneName.trim() === ''}
                loading={addZone.isPending}
                onClick={pushZone}
              >
                <Plus size={16} aria-hidden />
                {t('storeroom.zoneAdd')}
              </Button>
            </div>
          )}
        </section>

        {editing && storeroom.responsibleName !== null ? (
          <Badge tone="neutral">{t('storeroom.responsible', { name: storeroom.responsibleName })}</Badge>
        ) : null}
      </div>
    </Dialog>
  );
};
